import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { Accordion, AccordionItem } from '@carbon/react';
import { useAtom } from 'jotai';
import {
  CardSlot,
  LayoutSettings,
  RailSectionTitle,
  WorkspaceCard,
  countVisible,
  isCardVisible,
  useDockOffsets,
  type LayoutSettingsGroup,
} from 'components/workspace-cards';
import {
  pickSections,
  useWellPageSections,
  type WellPageSection,
  type WellPageSectionId,
} from 'components/well-page-content/well-page-sections';
import { useWellCompletions } from 'lib/wells';
import type { WellsResponse } from 'types/api-responses';
import {
  WellsColorBySection,
  WellsColorRulesProvider,
  resolveTraceColors,
  wellPageColorRulesAtom,
} from 'components/wells-color-by';
import {
  WELL_PAGE_COLOR_ATTRIBUTES,
  wellPageColorOptions,
} from './color-by-well';
import { wellPageVisibleSectionsAtom } from './atoms';
import { WorkspacesDrawer } from './workspaces-drawer';
import { GeometryWorkspacesDrawer } from './geometry-workspaces-drawer';
import {
  WellVersionRow,
  WellVersionsDrawer,
  useWellVersions,
} from './version-drawer';
import styles from './well-page-cards.module.scss';

interface Props {
  well?: WellsResponse;
  uwi: string;
  versionId?: string;
  /** Hides the settings rail. Toggled from the page header, as the mock has it. */
  railCollapsed?: boolean;
  /** Opens the Workspaces drawer, from the header's View menu. */
  workspacesOpen?: boolean;
  onCloseWorkspaces?: () => void;
  /** Opens the Wellbore Geometry Workspaces drawer, from the same View menu. */
  geometryWorkspacesOpen?: boolean;
  onCloseGeometryWorkspaces?: () => void;
  /** Changes the version every card reads. Omitted by a host with no version row. */
  onVersionChange?: (versionId: string) => void;
}

/**
 * Render order: mock v13's card order, with the sections it has no card for kept where
 * the grid layout has always put them (`GRID_ORDER` in well-page-content).
 *
 * Not appended at the end: Wellbore Completions reads as part of the Wellbores section
 * it follows.
 */
const CARD_ORDER: WellPageSectionId[] = [
  'well-details',
  'well-status',
  'eur',
  'map',
  'permits',
  'unit-details',
  'wellbores',
  'wellbore-completions',
  'subsurface-explorer',
  'stimulations',
  'well-analysis',
  'related-items',
];

/**
 * The groups in the settings rail. Every section is in the one group, in the order
 * the cards are, so the toggle list reads the way the page does.
 */
const SECTION_GROUPS: Array<{
  legend: string;
  ids: WellPageSectionId[];
}> = [{ legend: 'Tables & details', ids: CARD_ORDER }];

/**
 * Card bodies that need a definite height because the section inside sizes itself off
 * its parent rather than off its content. A card's own height is its content's, which
 * leaves those sections resolving a percentage height against nothing.
 */
const BODY_CLASSES: Partial<Record<WellPageSectionId, string>> = {
  map: styles.mapBody,
};

/**
 * The Well page in the Well Workspace's layout: a left settings rail beside a single
 * column of section cards, each pinnable to the top.
 *
 * The workspace shell reduced to one well, so the tree and dark bar are absent. The
 * sections are {@link useWellPageSections}, shared with the grid layout.
 * @param {Props} props The well, its UWI and version, and whether the rail is hidden.
 * @return {JSX.Element} The card layout.
 */
export const WellPageCards: FC<Props> = ({
  well,
  uwi,
  versionId,
  railCollapsed,
  workspacesOpen,
  onCloseWorkspaces,
  geometryWorkspacesOpen,
  onCloseGeometryWorkspaces,
  onVersionChange,
}) => {
  // The version list, read once here: the rail's line needs the current version's name
  // and the drawer needs the whole list, and a hook in each would fetch it twice.
  const versions = useWellVersions(well, versionId);
  const [versionsOpen, setVersionsOpen] = useState(false);

  // Colour rules for the wells drawn on this page's surfaces, not just the subject well
  // — see wellPageColorOptions. An atom because the rail writes them and the Map card,
  // its sibling, reads them.
  const [colorRules, setColorRules] = useAtom(wellPageColorRulesAtom);
  // The atom is global and the route element is not remounted on a uwi change, so a
  // rule added on one well would keep painting the next. Cleared when the well changes
  // and when the page unmounts.
  useEffect(() => () => setColorRules([]), [well?.uwi, setColorRules]);
  const getColorOptions = useCallback(
    (attributeId: string) => wellPageColorOptions(well, attributeId),
    [well]
  );

  // This well's values, as the rules name them. One object so every surface resolves
  // against the same facts.
  const colorAttributes = useMemo(
    () => ({
      status: well?.status ?? null,
      currentOperator: well?.currentOperator ?? null,
      srpLandingZone: well?.srpLandingZone ?? null,
    }),
    [well]
  );

  // What the Map should draw each of this well's traces in. Empty when no rule covers
  // it, which leaves the map its own default.
  const mapTraceColors = useMemo(
    () => resolveTraceColors(colorRules, 'map', colorAttributes),
    [colorRules, colorAttributes]
  );

  // For the dates in Wellbore Completions. Its card is hidden, never unmounted, so the
  // table is always mounted with the page and reading here changes nothing about when.
  const { data: completions, error: completionsError } = useWellCompletions(
    uwi,
    well?.resources.queries.wellCompletions?.href ?? ''
  );

  const sections = pickSections(
    useWellPageSections({
      well,
      uwi,
      versionId,
      completions,
      completionsError,
      // Not 'quad' yet, though mock v13 shows one: the quad has no cell for Landing
      // Analysis or Well Spacing, which this page's tabs do show, so switching would
      // lose function rather than change layout. Flip once those two have a home.
      subsurfaceLayout: 'tabs',
      // The Explorer's controls open from a gear in its card header into a drawer
      // inside the section (UAT), not from the rail.
      subsurfaceControls: 'drawer',
      // A card grows to its content like every other card here; the column scrolls.
      subsurfaceFitContent: true,
      mapTraceColors,
    }),
    CARD_ORDER
  );

  const [visibleSections, setVisibleSections] = useAtom(
    wellPageVisibleSectionsAtom
  );

  // Which cards are docked to the top. Any card can be pinned; the pinned ones tile down
  // from the top of the column in page order while the rest scroll underneath. Session
  // state, as the workspace has it — nothing is persisted.
  const [pinnedCards, setPinnedCards] = useState<Set<string>>(new Set());
  const togglePinnedCard = useCallback((sectionId: string) => {
    setPinnedCards((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);

      return next;
    });
  }, []);
  // Re-measured when a section is hidden as well as when one is pinned: hiding a pinned
  // card drops it from the dock without touching `pinnedCards`.
  const dockOffsets = useDockOffsets(
    pinnedCards,
    JSON.stringify(visibleSections)
  );

  // Docking needs a pane that scrolls rather than the document scrolling under it, so
  // the shell is bounded to the viewport while this page is mounted and its panes
  // scroll internally. Scoped by a class and removed on unmount, as map-query does.
  useEffect(() => {
    const shell = document.getElementById('main-content');
    shell?.classList.add('well-page-cards-bounded');

    return () => shell?.classList.remove('well-page-cards-bounded');
  }, []);

  const isVisible = (id: WellPageSectionId) =>
    isCardVisible(visibleSections, id);

  // Only the sections that exist, so a group's count and its Show all never include an
  // id the builder did not produce.
  const groups: LayoutSettingsGroup[] = SECTION_GROUPS.map((group) => ({
    legend: group.legend,
    items: pickSections(sections, group.ids).map(({ id, label }) => ({
      id,
      label,
    })),
  }));
  const visibleCount = countVisible(
    visibleSections,
    groups.flatMap((group) => group.items)
  );

  const setGroupVisible = (ids: string[], visible: boolean) =>
    setVisibleSections((prev) => ({
      ...prev,
      ...Object.fromEntries(ids.map((id) => [id, visible])),
    }));

  return (
    // Publishes this page's rules to the Subsurface Explorer nested below, which paints
    // three of the four targets and is shared with hosts that have no rules at all.
    <WellsColorRulesProvider rules={colorRules}>
      <div className={styles.shell}>
        {!railCollapsed && (
          <aside className={styles.rail}>
            <Accordion className={styles.accordion}>
              {/* Mock v13 opens this rail with a Global Map Filter carried over from the
                wells listing. It is left out: a spatial filter has nothing to act on
                for one well. With product on whether it belongs here at all. */}
              <AccordionItem
                open
                title={
                  <RailSectionTitle label="Wells" count={colorRules.length} />
                }
              >
                <div className={styles.sectionBody}>
                  <WellsColorBySection
                    categories={WELL_PAGE_COLOR_ATTRIBUTES}
                    getOptions={getColorOptions}
                    rules={colorRules}
                    onChange={setColorRules}
                  />
                </div>
              </AccordionItem>

              <AccordionItem
                open
                title={
                  <RailSectionTitle
                    label="Layout settings"
                    count={visibleCount}
                  />
                }
              >
                {/* Above the card toggles, as mock v13 has it: which version is on
                    screen is part of how the page is being read, next to which cards
                    are showing. The drawer it opens is rendered at the shell level
                    below, so it lands on the right of the page rather than inside
                    this rail. */}
                {well && onVersionChange && (
                  <WellVersionRow
                    label={versions.showingLabel}
                    loading={versions.loading}
                    onOpen={() => setVersionsOpen(true)}
                  />
                )}
                <LayoutSettings
                  groups={groups}
                  visible={visibleSections}
                  idPrefix="well-section-"
                  onToggle={(id, visible) =>
                    setVisibleSections((prev) => ({ ...prev, [id]: visible }))
                  }
                  onToggleGroup={setGroupVisible}
                />
              </AccordionItem>
            </Accordion>
          </aside>
        )}

        {/* A named region rather than <main>: this sits inside the app shell's own
          <main id="main-content">, and a document may only have one. */}
        <section aria-label="Well sections" className={styles.main}>
          {sections.map((section: WellPageSection) => {
            const bodyClass = BODY_CLASSES[section.id];
            // Every card draws its own heading here; a section that owns one in the
            // grid supplies a `cardBody` without it.
            const body = section.cardBody ?? section.body;

            return (
              <CardSlot
                key={section.id}
                sectionId={section.id}
                pinnedIds={pinnedCards}
                onTogglePin={togglePinnedCard}
                stickyTop={dockOffsets[section.id] ?? 0}
                // Hidden, never unmounted — unmounting these sections is destructive and
                // a section comes back as the reader left it; see CardSlot's `hidden`.
                hidden={!isVisible(section.id)}
              >
                {/* No `scope`: the workspace's cards each name the well they are scoped
                  to because the reader flips between wells there. This page is one
                  well, named once in its PageHeader, so repeating it on every card
                  would be noise and its steppers would have nowhere to go. */}
                <WorkspaceCard title={section.label} actions={section.actions}>
                  {bodyClass ? <div className={bodyClass}>{body}</div> : body}
                </WorkspaceCard>
              </CardSlot>
            );
          })}
        </section>

        {/* Inside the shell, so it pushes the card column over rather than floating
          above it — the way mock v13's drawers behave. */}
        <WorkspacesDrawer
          uwi={uwi}
          open={Boolean(workspacesOpen)}
          onClose={onCloseWorkspaces ?? (() => undefined)}
        />
        <GeometryWorkspacesDrawer
          uwi={uwi}
          open={Boolean(geometryWorkspacesOpen)}
          onClose={onCloseGeometryWorkspaces ?? (() => undefined)}
        />

        {/* A sibling of the rail and the card column, not a child of the rail — so it
            opens against the right edge of the page, beside the Workspaces drawer,
            rather than inside the settings panel. */}
        {versionsOpen && onVersionChange && (
          <WellVersionsDrawer
            versions={versions.rows}
            failed={versions.failed}
            loading={versions.loading}
            selectedId={versionId ?? well?.versionId}
            onClose={() => setVersionsOpen(false)}
            onApply={(next) => {
              onVersionChange(next);
              setVersionsOpen(false);
            }}
          />
        )}
      </div>
    </WellsColorRulesProvider>
  );
};
